import { sortBy } from "lodash";
import { CollectionNode, CollectionNodeObject, SelectionStateWithSequence } from "types";
import { isProductCollectionNode } from "@/models/CollectionNode";

interface AddCollectionFormState {
    path: string;
    title: string;
    description: string;
    items: SelectionStateWithSequence;
}

interface CreateCollectionPayload {
    path?: string;
    seller?: { id: ID };
    name?: string;
    description?: string;
    object?: CollectionNodeObject;
    product?: { id: ID };
    sequence?: number;
    children?: Array<{
        id?: ID;
        object: CollectionNodeObject;
        product?: { id: ID };
        sequence?: number;
        _remove?: boolean;
    }>;
}

export const getInitialValues = (
    collection: CollectionNode | undefined,
    items: SelectionStateWithSequence["selected"],
): AddCollectionFormState => {
    return {
        path: collection?.path ?? "",
        title: collection?.name ?? "",
        description: collection?.description ?? "",
        items: {
            all: false,
            selected: items,
        },
    };
};

// Helper function to extract product collection nodes into selection state
export const extractProductCollectionNode = (productCollectionNode: CollectionNode[]) => {
    const items: SelectionStateWithSequence["selected"] = [];
    const collectionItemsMap = new Map<ID, { collectionItemId: ID; sequence: number }>();

    const orderedProductCollection = sortBy(productCollectionNode, "sequence");

    for (const productCollection of orderedProductCollection) {
        if (isProductCollectionNode(productCollection) && productCollection.product?.id) {
            const productId = productCollection.product.id;
            items.push({
                id: productId,
                sequence: productCollection.sequence,
            });
            collectionItemsMap.set(productId, {
                collectionItemId: productCollection.id,
                sequence: productCollection.sequence,
            });
        }
    }

    return { items, itemsMap: collectionItemsMap };
};

// Helper function to calculate product collection item changes for PATCH
export const calculateItemChanges = (
    existingItemsMap: Map<ID, { collectionItemId: ID; sequence: number }>,
    newItems: SelectionStateWithSequence["selected"],
): Array<{
    id?: ID;
    object: CollectionNodeObject;
    product?: { id: ID };
    sequence?: number;
    _remove?: boolean;
}> => {
    const existingProductIds = new Set(existingItemsMap.keys());
    const newProductIdsSet = new Set(newItems.map((item) => item.id));
    const childrenUpdates: Array<{
        id?: ID;
        object: CollectionNodeObject;
        product?: { id: ID };
        sequence?: number;
        _remove?: boolean;
    }> = [];

    // Find removed items
    for (const existingProductId of existingProductIds) {
        if (!newProductIdsSet.has(existingProductId)) {
            const existingItem = existingItemsMap.get(existingProductId);
            if (existingItem) {
                childrenUpdates.push({
                    id: existingItem.collectionItemId,
                    object: CollectionNodeObject.PRODUCT_COLLECTION_NODE,
                    _remove: true,
                });
            }
        }
    }

    // Find added items and items with changed sequence
    for (const item of newItems) {
        const existingItem = existingItemsMap.get(item.id);

        if (!existingItem) {
            // New item
            childrenUpdates.push({
                object: CollectionNodeObject.PRODUCT_COLLECTION_NODE,
                product: { id: item.id },
                sequence: item.sequence,
            });
        } else if (existingItem.sequence !== item.sequence) {
            // Existing item with changed sequence
            childrenUpdates.push({
                id: existingItem.collectionItemId,
                object: CollectionNodeObject.PRODUCT_COLLECTION_NODE,
                sequence: item.sequence,
            });
        }
    }

    return childrenUpdates;
};

// Helper function to build PATCH payload
export const getPatchPayload = (
    values: AddCollectionFormState,
    existingCollection: CollectionNode,
    existingItemsMap: Map<ID, { collectionItemId: ID; sequence: number }>,
): CreateCollectionPayload => {
    const payload: CreateCollectionPayload = {};

    // Only include changed fields
    if (values.path !== existingCollection.path) {
        payload.path = values.path;
    }
    if (values.title.trim() !== existingCollection.name) {
        payload.name = values.title.trim();
    }
    if (values.description.trim() !== (existingCollection.description ?? "")) {
        payload.description = values.description.trim() || undefined;
    }

    // Handle item changes
    const childrenUpdates = calculateItemChanges(existingItemsMap, values.items.selected);

    // Only include children if there are changes
    if (childrenUpdates.length > 0) {
        payload.children = childrenUpdates;
    }

    return payload;
};

// Helper function to get POST payload
export const getPostPayload = (values: AddCollectionFormState, sellerId: ID): CreateCollectionPayload => {
    const payload: CreateCollectionPayload = {
        path: values.path,
        seller: { id: sellerId },
        name: values.title.trim(),
        description: values.description.trim() || undefined,
        object: CollectionNodeObject.GROUP_COLLECTION_NODE,
    };

    // Include children in the initial payload for POST
    if (values.items.selected.length > 0) {
        payload.children = values.items.selected.map((item) => ({
            object: CollectionNodeObject.PRODUCT_COLLECTION_NODE,
            product: { id: item.id },
            sequence: item.sequence,
        }));
    }

    return payload;
};
